import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Container, Row, Col, Card, CardBody, Table,
    Button, Badge, Spinner
} from "reactstrap";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import dayjs from "dayjs";

const CreditNoteView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [data, setData] = useState(null);

    useEffect(() => {
        fetchDetails();
    }, [id]);

    const fetchDetails = async () => {
        dispatch(loadingTrue());
        try {
            const res = await Handler({
                method: "GET",
                url: `/credit_note/view.php?id=${id}`
            });
            if (res?.success) {
                setData(res.data);
            } else {
                toast.error(res?.message || "Failed to load details");
            }
        } catch (err) {
            toast.error("Error connecting to server");
        } finally {
            dispatch(loadingFalse());
        }
    };

    if (!data) return <div className="p-5 text-center"><Spinner color="danger" /></div>;

    const { credit_note, client, invoice_current } = data;

    return (
        <Container className="py-4 px-2 bg-light min-vh-100">
            {/* Action Bar */}
            <div className="d-flex justify-content-between align-items-center mb-4 no-print">
                <Button color="secondary" outline size="sm" onClick={() => navigate(-1)}>
                    <i className="bx bx-left-arrow-alt"></i> Back to List
                </Button>
                {/* <Button color="danger" size="sm" onClick={() => window.print()}>
                    <i className="bx bx-printer"></i> Print / Save PDF
                </Button> */}
            </div>

            {/* Document Layout */}
            <Card className="border-0 shadow-sm mx-auto shadow-lg" style={{ maxWidth: "850px", borderRadius: "0" }}>
                <CardBody className="p-5">
                    {/* Header Section */}
                    <Row className="mb-4 border-bottom pb-3">
                        <Col md={8}>
                            <h2 className="fw-bold text-dark mb-1">MUTHIAH COTTON MILL</h2>
                            <p className="text-muted small mb-0">
                                Cotton & Cotton Seeds Merchant<br />
                                <strong>GSTIN:</strong> 33QDPTS4652E1ZT
                            </p>
                        </Col>
                        <Col md={4} className="text-md-end mt-3 mt-md-0">
                            <h4 className="text-danger fw-bold mb-1">CREDIT NOTE</h4>
                            <div className="small text-dark">
                                <strong>No:</strong> {credit_note.credit_note_no || 'N/A'}<br />
                                <strong>Date:</strong> {dayjs(credit_note.created_at).format("DD-MM-YYYY")}
                            </div>
                        </Col>
                    </Row>

                    {/* Client & Invoice Info */}
                    <Row className="mb-5 g-4">
                        <Col md={6}>
                            <h6 className="text-uppercase small fw-bold text-muted mb-2">Client Details:</h6>
                            <div className="ps-2 border-start border-danger border-3">
                                <h5 className="fw-bold mb-1 text-dark text-uppercase">{client.name}</h5>
                                <p className="small text-muted mb-1">{client.address || 'Address not available'}</p>
                                <p className="small mb-0"><strong>GSTIN:</strong> {client.gstin || 'N/A'}</p>
                            </div>
                        </Col>
                        <Col md={6} className="text-md-end">
                            <h6 className="text-uppercase small fw-bold text-muted mb-2">Reference Info:</h6>
                            <div className="small">
                                <p className="mb-1"><strong>Invoice No:</strong> <span className="text-danger">#{invoice_current.invoice_no}</span></p>
                                <p className="mb-1"><strong>Adjustment Type:</strong> <Badge color="soft-danger" className="text-uppercase">{credit_note.type}</Badge></p>
                                <p className="mb-0"><strong>Rate / KG:</strong> ₹{invoice_current.rate_per_kg}</p>
                            </div>
                        </Col>
                    </Row>

                    {/* Table Section */}
                    <Table bordered responsive className="mb-4 text-center align-middle">
                        <thead className="bg-light">
                            <tr className="small text-uppercase fw-bold">
                                <th className="text-start">Description</th>
                                <th style={{ width: "15%" }}>Reduced KG</th>
                                <th style={{ width: "15%" }}>Reduced Qtl</th>
                                <th style={{ width: "20%" }}>Base Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="text-start py-3">
                                    <div className="fw-bold">Credit Adjustment</div>
                                    <div className="small text-muted mt-1 italic">Reason: {credit_note.reason || 'Weight correction'}</div>
                                </td>
                                <td className="fw-bold">{credit_note.reduced_kg}</td>
                                <td>{credit_note.reduced_quintal}</td>
                                <td className="text-end fw-bold">₹{credit_note.credit.base_amount.toLocaleString()}</td>
                            </tr>
                        </tbody>
                    </Table>

                    {/* Calculation Summary */}
                    <Row className="justify-content-between g-4">
                        <Col md={7}>
                            <div className="p-3 bg-light rounded-3" style={{ borderLeft: '4px solid #dc3545' }}>
                                <small className="text-muted text-uppercase fw-bold d-block mb-1">Amount in Words:</small>
                                <h6 className="fw-bold text-dark text-uppercase small">
                                    {/* You can use a library like to-words or hardcode for now */}
                                    RUPEES {credit_note.credit.total_credit_amount?.toLocaleString()} ONLY
                                </h6>
                            </div>
                            <div className="mt-5 pt-4">
                                <div className="text-dark small mb-5">For MUTHIAH COTTON MILL</div>
                                <div className="mt-4 pt-2 border-top d-inline-block px-4 small fw-bold">Authorized Signatory</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="border rounded shadow-sm overflow-hidden">
                                <div className="d-flex justify-content-between p-2 border-bottom">
                                    <span className="small text-muted">Base Credit:</span>
                                    <span className="fw-bold">₹{credit_note.credit.base_amount?.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between p-2 border-bottom bg-light">
                                    <span className="small text-muted">GST ({credit_note.credit.gst_percentage}%):</span>
                                    <span className="fw-bold text-primary">₹{credit_note.credit.gst_amount?.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between p-2 bg-danger text-white">
                                    <span className="small">Total Credit Amount:</span>
                                    <span className="fw-bold fs-5">₹{credit_note.credit.total_credit_amount?.toLocaleString()}</span>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Print Styling */}
            <style>
                {`
                    @media print {
                        .no-print { display: none !important; }
                        body { background: white !important; }
                        .card { box-shadow: none !important; border: 1px solid #ddd !important; }
                        .page-container { padding: 0 !important; }
                    }
                `}
            </style>
        </Container>
    );
};

export default CreditNoteView;